import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '../../store/authStore';
import api from '../../services/api';
import toast from 'react-hot-toast';
import {
  ArrowLeftIcon,
  DocumentTextIcon,
  TableCellsIcon,
  GlobeAltIcon,
  ServerIcon,
  CircleStackIcon,
  CheckCircleIcon,
  MagnifyingGlassIcon,
  SparklesIcon
} from '@heroicons/react/24/outline';

// Import source type components
import FileUploadSource from '../../components/datasource/FileUploadSource';
import RestApiSource from '../../components/datasource/RestApiSource';
import DatabaseSource from '../../components/datasource/DatabaseSource';
import GoogleSheetsSource from '../../components/datasource/GoogleSheetsSource';
import GoogleAnalyticsSource, { GoogleAnalyticsIcon } from '../../components/datasource/GoogleAnalyticsSource';
import WebScraperSource from '../../components/datasource/WebScraperSource';
import AIResearchSource from '../../components/datasource/AIResearchSource';

// Google Sheets icon component
const GoogleSheetsIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M19.385 2H4.615C3.17 2 2 3.17 2 4.615v14.77C2 20.83 3.17 22 4.615 22h14.77c1.445 0 2.615-1.17 2.615-2.615V4.615C22 3.17 20.83 2 19.385 2zM8 18H6v-2h2v2zm0-4H6v-2h2v2zm0-4H6V8h2v2zm10 8h-8v-2h8v2zm0-4h-8v-2h8v2zm0-4h-8V8h8v2z"/>
  </svg>
);

const SOURCE_TYPES = [
  { 
    id: 'csv', 
    name: 'CSV File', 
    description: 'Upload a comma-separated values file',
    icon: DocumentTextIcon,
    color: 'text-blue-500',
    bgColor: 'bg-blue-100 dark:bg-blue-900/30'
  },
  { 
    id: 'excel', 
    name: 'Excel File', 
    description: 'Upload an Excel spreadsheet (.xlsx, .xls)',
    icon: TableCellsIcon,
    color: 'text-green-500',
    bgColor: 'bg-green-100 dark:bg-green-900/30'
  },
  { 
    id: 'api', 
    name: 'REST API', 
    description: 'Connect to a REST API endpoint',
    icon: GlobeAltIcon,
    color: 'text-amber-500',
    bgColor: 'bg-amber-100 dark:bg-amber-900/30'
  },
  { 
    id: 'postgresql', 
    name: 'PostgreSQL', 
    description: 'Connect to a PostgreSQL database',
    icon: ServerIcon,
    color: 'text-indigo-500',
    bgColor: 'bg-indigo-100 dark:bg-indigo-900/30'
  },
  { 
    id: 'mysql', 
    name: 'MySQL', 
    description: 'Connect to a MySQL database',
    icon: ServerIcon,
    color: 'text-orange-500',
    bgColor: 'bg-orange-100 dark:bg-orange-900/30'
  },
  { 
    id: 'mongodb', 
    name: 'MongoDB', 
    description: 'Connect to MongoDB or MongoDB Atlas',
    icon: CircleStackIcon,
    color: 'text-emerald-500',
    bgColor: 'bg-emerald-100 dark:bg-emerald-900/30'
  },
  { 
    id: 'googlesheets', 
    name: 'Google Sheets', 
    description: 'Import data from Google Sheets',
    icon: GoogleSheetsIcon,
    color: 'text-green-600',
    bgColor: 'bg-green-100 dark:bg-green-900/30'
  },
  { 
    id: 'googleanalytics', 
    name: 'Google Analytics', 
    description: 'Import data from Google Analytics 4',
    icon: GoogleAnalyticsIcon,
    color: 'text-orange-500',
    bgColor: 'bg-orange-100 dark:bg-orange-900/30'
  },
  { 
    id: 'webscraper', 
    name: 'Web Scraper', 
    description: 'Scrape & extract structured data from any public webpage',
    icon: MagnifyingGlassIcon,
    color: 'text-purple-500',
    bgColor: 'bg-purple-100 dark:bg-purple-900/30'
  },
  { 
    id: 'airesearch', 
    name: 'AI Research', 
    description: 'Generate structured data from AI-powered web research',
    icon: SparklesIcon,
    color: 'text-cyan-500',
    bgColor: 'bg-cyan-100 dark:bg-cyan-900/30'
  }
];

export default function DataSourceCreate() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const { getCurrentCompanyId, isSuperUser, getCurrentCompany } = useAuthStore();
  
  const initialType = searchParams.get('type');
  const [selectedType, setSelectedType] = useState(initialType || null);
  const [step, setStep] = useState(initialType ? 2 : 1);
  
  const currentCompany = getCurrentCompany();
  const companyId = getCurrentCompanyId();
  
  // Check if company is selected for super users
  useEffect(() => {
    if (isSuperUser() && !companyId) {
      toast.error('Please select a company first');
      navigate('/admin/companies');
    }
  }, [isSuperUser, companyId, navigate]);
  
  const handleTypeSelect = (type) => {
    setSelectedType(type);
    setStep(2);
  };
  
  const handleBack = () => {
    if (step === 2) {
      setSelectedType(null);
      setStep(1);
    } else {
      navigate('/data');
    }
  };
  
  const handleSuccess = (dataSource) => {
    queryClient.invalidateQueries({ queryKey: ['dataSources'] });
    toast.success('Data source created successfully!');
    navigate(`/data/${dataSource._id}`);
  };
  
  const selectedTypeInfo = SOURCE_TYPES.find(t => t.id === selectedType);
  
  const renderSourceConfig = () => {
    switch (selectedType) {
      case 'csv':
      case 'excel':
        return (
          <FileUploadSource 
            type={selectedType} 
            companyId={companyId}
            onSuccess={handleSuccess}
            onBack={handleBack}
          />
        );
      case 'api':
        return (
          <RestApiSource 
            companyId={companyId}
            onSuccess={handleSuccess}
            onBack={handleBack}
          />
        );
      case 'postgresql':
      case 'mysql':
      case 'mongodb':
        return (
          <DatabaseSource 
            type={selectedType}
            companyId={companyId}
            onSuccess={handleSuccess}
            onBack={handleBack}
          />
        );
      case 'googlesheets':
        return (
          <GoogleSheetsSource 
            companyId={companyId}
            onSuccess={handleSuccess}
            onBack={handleBack}
          />
        );
      case 'googleanalytics':
        return (
          <GoogleAnalyticsSource 
            companyId={companyId}
            onSuccess={handleSuccess}
            onBack={handleBack}
          />
        );
      case 'webscraper':
        return (
          <WebScraperSource 
            companyId={companyId}
            onSuccess={handleSuccess}
            onBack={handleBack}
          />
        );
      case 'airesearch':
        return (
          <AIResearchSource 
            companyId={companyId}
            onSuccess={handleSuccess}
            onBack={handleBack}
          />
        );
      default:
        return null;
    }
  };
  
  return (
    <div className="max-w-5xl mx-auto animate-fade-in">
      {/* Header */}
      <div className="flex items-center gap-4 mb-8">
        <button 
          onClick={handleBack}
          className="p-2 rounded-lg hover:bg-surface-100 dark:hover:bg-surface-800"
        >
          <ArrowLeftIcon className="w-5 h-5 text-surface-600 dark:text-surface-400" />
        </button>
        <div>
          <h1 className="text-2xl font-display font-bold text-surface-900 dark:text-white">
            {step === 1 ? 'Add Data Source' : selectedTypeInfo?.name}
          </h1>
          <p className="text-surface-500 dark:text-surface-400">
            {step === 1 
              ? 'Choose the type of data source to connect'
              : selectedTypeInfo?.description
            }
          </p>
        </div>
      </div>
      
      {/* Company context for super users */}
      {isSuperUser() && currentCompany && (
        <div className="mb-6 bg-primary-50 dark:bg-primary-900/20 border border-primary-200 dark:border-primary-800 rounded-xl p-3 flex items-center gap-3">
          <CircleStackIcon className="w-5 h-5 text-primary-600 dark:text-primary-400" />
          <span className="text-sm text-primary-900 dark:text-primary-100">
            Adding data source to <strong>{currentCompany.name}</strong>
          </span>
        </div>
      )}
      
      {/* Step 1: Type Selection */}
      {step === 1 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {SOURCE_TYPES.map((type) => (
            <button
              key={type.id}
              onClick={() => handleTypeSelect(type.id)}
              className="card p-6 text-left hover:border-primary-500 hover:shadow-lg transition-all group"
            >
              <div className={`w-12 h-12 rounded-xl ${type.bgColor} flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}>
                <type.icon className={`w-6 h-6 ${type.color}`} />
              </div>
              <h3 className="font-semibold text-surface-900 dark:text-white mb-1">
                {type.name}
              </h3>
              <p className="text-sm text-surface-500 dark:text-surface-400">
                {type.description}
              </p>
            </button>
          ))}
        </div>
      )}
      
      {/* Step 2: Source Configuration */}
      {step === 2 && renderSourceConfig()}
    </div>
  );
}

